import React, { useEffect, useRef } from "react";

/* Renders today's BSL sign as a small canvas with the white
   background of the source PNG knocked out, so the figure sits
   directly on whatever panel colour we pick. Used in the mega-menu
   feature tile and anywhere else we need a clean miniature of the
   day's sign. */

const proxyUrl = (raw) => raw;

const RASTER_RE = /\.(png|jpe?g)$/i;

const SIZE = 280; // square canvas - scales with CSS

const SotdMenuThumb = ({ image, alt = "" }) => {
  const canvasRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    const canvas = canvasRef.current;
    if (!canvas || !image) return undefined;
    const ctx = canvas.getContext("2d");
    canvas.width = SIZE;
    canvas.height = SIZE;
    ctx.clearRect(0, 0, SIZE, SIZE);

    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      if (cancelled) return;
      // Step 1: draw onto an offscreen canvas so we can read pixels.
      const oc = document.createElement("canvas");
      oc.width = img.width;
      oc.height = img.height;
      const octx = oc.getContext("2d");
      octx.drawImage(img, 0, 0);
      try {
        const data = octx.getImageData(0, 0, oc.width, oc.height);
        const px = data.data;
        for (let i = 0; i < px.length; i += 4) {
          const r = px[i];
          const g = px[i + 1];
          const b = px[i + 2];
          if (r >= 240 && g >= 240 && b >= 240) {
            px[i + 3] = 0;
          }
        }
        octx.putImageData(data, 0, 0);
      } catch (e) {
        /* tainted - fall through and just draw the raw image */
      }
      // Step 2: fit the cleaned figure into the target canvas.
      const pad = 8;
      const innerW = SIZE - pad * 2;
      const innerH = SIZE - pad * 2;
      const ratio = Math.min(innerW / oc.width, innerH / oc.height);
      const drawW = oc.width * ratio;
      const drawH = oc.height * ratio;
      const drawX = (SIZE - drawW) / 2;
      const drawY = pad + (innerH - drawH) / 2;
      ctx.drawImage(oc, drawX, drawY, drawW, drawH);
    };
    // Prefer the same-origin AVIF sibling (already cached from the page's
    // <Picture> tags) so the menu thumb doesn't also pull the big PNG; fall
    // back to the original PNG/JPG if AVIF can't be decoded. AVIF is
    // same-origin, so getImageData (the white-knockout above) stays untainted.
    const isLocalRaster = !image.startsWith("http") && RASTER_RE.test(image);
    let usedFallback = false;
    img.onerror = () => {
      if (isLocalRaster && !usedFallback) {
        usedFallback = true;
        img.src = image; // fall back to the original raster
      }
      /* else leave blank; the parent shows the rest of the tile */
    };
    img.src = isLocalRaster
      ? image.replace(RASTER_RE, ".avif")
      : image.startsWith("http")
      ? proxyUrl(image)
      : image;
    return () => {
      cancelled = true;
    };
  }, [image]);

  return (
    <canvas
      ref={canvasRef}
      className="bs-sotd-menu-thumb"
      role="img"
      aria-label={alt}
    />
  );
};

export default SotdMenuThumb;
